import type {CSSProperties} from "aphrodite";

import {color, fade, mix} from "@khanacademy/wonder-blocks-tokens";
import {doNotTranslateYet} from "@khan/i18n";
import {Errors, KAError} from "@khan/logging";

import type {ComputingCodeMultiFile} from "#content-library/types/types.ts";

export function createPrimaryButtonStyle(
    backgroundColor: string,
): CSSProperties {
    const activeBackgroundColor = mix(color.offBlack32, backgroundColor);
    const activeTextColor = mix(fade(backgroundColor, 0.32), color.white);
    return {
        backgroundColor,
        color: color.white,
        ":hover": {
            boxShadow: `0 0 0 1px ${color.white}, 0 0 0 3px ${backgroundColor}`,
        },
        ":focus": {
            outline: "none",
            boxShadow: `0 0 0 1px ${color.white}, 0 0 0 3px ${backgroundColor}`,
        },
        ":active": {
            backgroundColor: activeBackgroundColor,
            boxShadow: `0 0 0 1px ${color.white}, 0 0 0 3px ${activeBackgroundColor}`,
            color: activeTextColor,
        },
    };
}

export function createFile(
    code: ComputingCodeMultiFile,
    filename: string,
): ComputingCodeMultiFile {
    if (code.files.some((file) => file.filename === filename)) {
        throw new KAError(
            `file with name ${filename} already exists`,
            Errors.Internal,
        );
    }
    return {
        files: [
            ...code.files,
            {
                code: "",
                filename,
                language: "python",
            },
        ],
    };
}

export function deleteFile(
    code: ComputingCodeMultiFile,
    filename: string,
): ComputingCodeMultiFile {
    if (code.files.every((file) => file.filename !== filename)) {
        throw new KAError(
            `file with name ${filename} does not exist`,
            Errors.Internal,
        );
    }
    return {
        files: code.files.filter((file) => file.filename !== filename),
    };
}

export function renameFile(
    code: ComputingCodeMultiFile,
    oldFilename: string,
    newFilename: string,
): ComputingCodeMultiFile {
    if (code.files.every((file) => file.filename !== oldFilename)) {
        throw new KAError(
            `file with name ${oldFilename} does not exist`,
            Errors.Internal,
        );
    }
    if (code.files.some((file) => file.filename === newFilename)) {
        throw new KAError(
            `file with name ${newFilename} already exists`,
            Errors.Internal,
        );
    }
    return {
        files: code.files.map((file) =>
            file.filename === oldFilename
                ? {...file, filename: newFilename}
                : file,
        ),
    };
}

export const pythonFileExtension = ".py";

export function checkFilenameCriteria(
    editedFilenameWithoutExtension: string,
    currentFilename: string | null | undefined,
    filenames: Set<string>,
): Array<{description: string; isSatisfied: boolean}> {
    const editedFilename = `${editedFilenameWithoutExtension}${pythonFileExtension}`;
    return [
        {
            description: doNotTranslateYet`cannot be empty`,
            isSatisfied: editedFilenameWithoutExtension !== "",
        },
        {
            description: doNotTranslateYet`must only contain lowercase letters and “_”`,
            isSatisfied: /^[a-z_]*$/.test(editedFilenameWithoutExtension),
        },
        {
            description: doNotTranslateYet`cannot start or end with “_”`,
            isSatisfied:
                !editedFilenameWithoutExtension.startsWith("_") &&
                !editedFilenameWithoutExtension.endsWith("_"),
        },
        {
            description: doNotTranslateYet`cannot be longer than 25 characters`,
            isSatisfied: editedFilenameWithoutExtension.length <= 25,
        },
        {
            description: doNotTranslateYet`cannot be the same as another filename`,
            isSatisfied:
                editedFilename === currentFilename ||
                !filenames.has(editedFilename),
        },
    ];
}
